import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import { HOME_CONNECT_PDF } from '../../constants/shared/PdfGenerationConstants';
import Button from '../../components/button/Button';
import SimpleRow from '../../components/simpleRow/SimpleRow';
import HomeConnectRow from './HomeConnectRow';
import Checkbox from '../../components/checkbox/Checkbox';
import { LANGUAGE, PERIOD } from '../../constants/shared/CommonConstants';
import NavigationBar from '../navigation/NavigationBar';
import { getDisabledTabIdsForPeriod } from '../../utils/shared/navigationUtils';
import { HOME_CON_TABLE } from '../../constants/reading/DemoModeConstants';

import './HomeConnect.scss';

const { ENGLISH, SPANISH } = LANGUAGE;
export const DOWNLOAD_HOME_CONNECT_MESSAGE = `Download a summary of student assessment results to send home or use them as a basis for discussion at parent-teacher conferences.`;

const studentRowsWithAvailableMeasures = studentRows =>
    studentRows.filter(studentRow => !isEmpty(studentRow.availableMeasures));

const isAllChecked = (studentRows, type) =>
    studentRowsWithAvailableMeasures(studentRows).every(studentRow =>
        type === ENGLISH ? studentRow.isEnglishChecked : studentRow.isSpanishChecked,
    );

const isSomeChecked = studentRows =>
    studentRowsWithAvailableMeasures(studentRows).some(
        studentRow => studentRow.isEnglishChecked || studentRow.isSpanishChecked,
    );

const updateDownloadButtonState = (studentRows, isPdfDownloading) =>
    !isSomeChecked(studentRows) || isPdfDownloading;

const updateStudentRows = (studentRows, isChecked, type, studentId) =>
    studentRows.map(studentRow => {
        if (
            (!studentId && !isEmpty(studentRow.availableMeasures)) ||
            studentRow.studentInfo.id === studentId
        ) {
            return type === ENGLISH
                ? {
                      ...studentRow,
                      isEnglishChecked: isChecked,
                  }
                : {
                      ...studentRow,
                      isSpanishChecked: isChecked,
                  };
        }
        return studentRow;
    });

const updateOverallCheckboxState = (
    type,
    studentsList,
    isOverallEnglishChecked,
    isOverallSpanishChecked,
) => {
    const isOverallChecked = isAllChecked(studentsList, type);

    return {
        isOverallEnglishChecked: type === ENGLISH ? isOverallChecked : isOverallEnglishChecked,
        isOverallSpanishChecked: type !== ENGLISH ? isOverallChecked : isOverallSpanishChecked,
    };
};

const HomeConnect = ({
    classOrGroupInfo,
    period,
    generateHCPDF,
    currentPeriod,
    switchPeriod,
    LinkWrapper,
    isPdfDownloading,
    studentsData,
    isGuidedTourOn,
    toyTabs,
}) => {
    const [studentRows, setStudentRows] = useState(studentsData);
    const [isOverallEnglishChecked, setOverallEnglishChecked] = useState(false);
    const [isOverallSpanishChecked, setOverallSpanishChecked] = useState(false);
    const [isDownloadButtonDisabled, setDownloadButtonDisabled] = useState(true);
    const [isInfoBlockOpen, setIsInfoBlockOpen] = useState(period === PERIOD.SUMMER);

    useEffect(() => {
        setStudentRows(studentsData);
        setOverallEnglishChecked(false);
        setOverallSpanishChecked(false);
        setDownloadButtonDisabled(true);
    }, [studentsData]);

    useEffect(() => {
        setDownloadButtonDisabled(isPdfDownloading || !isSomeChecked(studentRows));
    }, [isPdfDownloading]);

    useEffect(() => {
        setIsInfoBlockOpen(period === PERIOD.SUMMER);
    }, [period]);

    const handleCheckboxChanged = (isChecked, type, studentId) => {
        const updatedStudentRows = updateStudentRows(studentRows, isChecked, type, studentId);
        const isDownloadDisabled = updateDownloadButtonState(updatedStudentRows, isPdfDownloading);
        const {
            isOverallEnglishChecked: isEnglishChecked,
            isOverallSpanishChecked: isSpanishChecked,
        } = updateOverallCheckboxState(
            type,
            updatedStudentRows,
            isOverallEnglishChecked,
            isOverallSpanishChecked,
        );

        setStudentRows(updatedStudentRows);
        setOverallEnglishChecked(isEnglishChecked);
        setOverallSpanishChecked(isSpanishChecked);
        setDownloadButtonDisabled(isDownloadDisabled);
    };

    const getLanguageCheckbox = (language, isOverallChecked, selectedHCPeriod) => {
        const isAnyRowsHaveAvailableMeasures = !isEmpty(
            studentRowsWithAvailableMeasures(studentRows),
        );
        return (
            <>
                {language}
                {isAnyRowsHaveAvailableMeasures && selectedHCPeriod !== PERIOD.SUMMER && (
                    <Checkbox
                        id={language}
                        isChecked={isOverallChecked}
                        className="home-connect-checkbox"
                        handleCheckboxChanged={isChecked =>
                            handleCheckboxChanged(isChecked, language)
                        }
                    />
                )}
            </>
        );
    };

    const getSelectedStudentsData = () =>
        studentRows
            .filter(row => row.isEnglishChecked || row.isSpanishChecked)
            .map(({ studentInfo, isEnglishChecked, isSpanishChecked }) => ({
                id: studentInfo.id,
                isEnglishChecked,
                isSpanishChecked,
            }));

    return (
        <div className="container-wrap">
            <div className="tabs-container space-between">
                <NavigationBar
                    ulTagClassName="nav nav-tabs"
                    tabs={toyTabs}
                    selectedTabId={period}
                    onNavigationTabClick={switchPeriod}
                    disabledTabIds={getDisabledTabIdsForPeriod(currentPeriod)}
                    LinkWrapper={LinkWrapper}
                />
            </div>

            <div
                data-tour-step={HOME_CON_TABLE}
                className="content-container home-connect-container"
            >
                {isInfoBlockOpen && (
                    <div className="info-box">
                        <i className="icon-v2-messaging-info" />
                        <div className="body">
                            Home Connect letters are not available for this benchmark window.
                        </div>
                        <button
                            type="button"
                            className="btn-icon-ghost"
                            onClick={() => setIsInfoBlockOpen(false)}
                        >
                            <i alt="Close infobox" className="icon-v2-close" />
                        </button>
                    </div>
                )}
                <div className="home-connect-header-container">
                    <span className="home-connect-message">{DOWNLOAD_HOME_CONNECT_MESSAGE}</span>
                    <Button
                        dataTourStep="demo-home-connect-download-btn"
                        data-tour-allow-interaction={false}
                        className="btn home-connect-download-btn home-connect-download-pdf-btn"
                        onClick={() => {
                            generateHCPDF({
                                documentKey: HOME_CONNECT_PDF,
                                selectedStudentsData: getSelectedStudentsData(),
                                fileName: `HomeConnect-${classOrGroupInfo.name}-${period}`,
                            });
                        }}
                        text="Download PDF"
                        isDisabled={isGuidedTourOn || isDownloadButtonDisabled}
                    />
                </div>
                {/* Deprioritized for 9/1 */}
                {/* <span className="home-connect-message second">
                        Select the completed assesment results to include in the letter.
                    </span> */}
                <div className="home-connect-student-rows-container">
                    <div className="home-connect-student-rows">
                        <div className="home-connect-row-container">
                            <SimpleRow
                                columns={[
                                    'Student Name',
                                    getLanguageCheckbox(ENGLISH, isOverallEnglishChecked, period),
                                    getLanguageCheckbox(SPANISH, isOverallSpanishChecked, period),
                                ]}
                                rowClassNames="home-connect-header"
                                cellClassNames="home-connect-header-cell"
                            />
                            {studentRows.map((studentRow, index) => {
                                return (
                                    <HomeConnectRow
                                        key={`${studentRow.studentInfo.id}-${studentRow.isEnglishChecked}-${studentRow.isSpanishChecked}`}
                                        index={index}
                                        studentRow={studentRow}
                                        handleCheckboxChanged={handleCheckboxChanged}
                                        period={period}
                                    />
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

HomeConnect.defaultProps = {
    studentsData: [],
    classOrGroupInfo: {},
    currentPeriod: '',
    period: '',
    isPdfDownloading: false,
    isGuidedTourOn: false,
    toyTabs: [],
    generateHCPDF: () => {},
    switchPeriod: () => {},
    LinkWrapper: () => {},
};

HomeConnect.propTypes = {
    studentsData: PropTypes.array,
    classOrGroupInfo: PropTypes.object,
    currentPeriod: PropTypes.string,
    period: PropTypes.string,
    isPdfDownloading: PropTypes.bool,
    generateHCPDF: PropTypes.func,
    switchPeriod: PropTypes.func,
    LinkWrapper: PropTypes.func,
    isGuidedTourOn: PropTypes.bool,
    toyTabs: PropTypes.array,
};

export default HomeConnect;
